import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { UpdateUserDto } from "~/data/user/update-user.dto";

import { updatePassword, updateUser } from "~/data/user/user.service";

import { Field } from "../shared/field";
import { Input } from "../shared/input";
import { Button } from "../shared/button";
import { mask } from "../shared/utils/mask";
import { useUser } from "../shared/queries/user-query";

interface FormShape extends UpdateUserDto {
  country: string;
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export function ProfileForm() {
  const user = useUser();
  const form = useForm<FormShape>({
    defaultValues: {
      ...user,
      country: "Brasil",
      current_password: "",
      new_password: "",
      new_password_confirmation: "",
    },
  });
  const queryClient = useQueryClient();
  const updateUserMutation = useMutation({
    mutationFn: async (userData: FormShape) => {
      await Promise.all([
        updateUser(userData),
        userData.new_password &&
          updatePassword({
            current_password: userData.current_password,
            new_password: userData.new_password,
            new_password_confirmation: userData.new_password_confirmation,
          }),
      ]);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["user"] }),
  });

  return (
    <form
      onSubmit={form.handleSubmit(async (data) => {
        await updateUserMutation.mutateAsync(data);
        toast.success("Salvo!");
        form.resetField("current_password");
        form.resetField("new_password");
        form.resetField("new_password_confirmation");
      })}
      className="bg-[#FBFBFB] p-10 rounded-[10px] flex gap-10 flex-wrap"
      style={{ maxWidth: 1080 }}
    >
      <div style={{ flex: 2 }}>
        <h4 className="text-lg font-medium font-alternative text-[#5B5B5B] mb-8">
          Dados Pessoais
        </h4>

        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Nome completo" className="flex-1">
            <Input
              type="text"
              placeholder="Insira seu nome"
              {...form.register("name")}
            />
          </Field>
          <Field label="CPF">
            <Input
              type="text"
              placeholder="Número do documento"
              {...form.register("cpf_cnpj")}
            />
          </Field>

          <Field label="Telefone">
            <Input
              type="text"
              placeholder="( ) _____-____"
              {...form.register("phone", {
                onChange: mask((value) => {
                  value = value.slice(0, 15);
                  value = value.replace(/\D/g, "");

                  if (value.length <= 10) {
                    // Format as (XX) XXXX-XXXX
                    return value.replace(
                      /^(\d{2})(\d{4})(\d{0,4})$/,
                      "($1) $2-$3"
                    );
                  } else {
                    // Format as (XX) XXXXX-XXXX
                    return value.replace(
                      /^(\d{2})(\d{5})(\d{0,4})$/,
                      "($1) $2-$3"
                    );
                  }
                }),
              })}
            />
          </Field>
          <Field label="E-mail">
            <Input
              type="email"
              placeholder="Insira um e-mail comercial válido"
              {...form.register("email")}
            />
          </Field>

          <Field label="País">
            <Input {...form.register("country")} readOnly />
          </Field>
          <Field label="CEP">
            <Input
              type="text"
              placeholder="__.___-___"
              {...form.register("postal_code", {
                onChange: async (e) => {
                  const handler = mask((value) =>
                    value
                      .replace(/\D/g, "")
                      .replace(/^(\d{5})(\d)/, "$1-$2")
                      .slice(0, 9)
                  );
                  handler(e);

                  const cep = e.currentTarget.value;
                  if (cep.length < 9) return;
                  const res = await fetch(
                    `https://viacep.com.br/ws/${cep}/json/`
                  );
                  if (!res.ok) return;
                  const json = await res.json();
                  form.setValue("street", json.logradouro);
                  form.setValue("province", json.bairro);
                  //   form.setValue("city", json.localidade);
                  //   form.setValue("state", json.estado);
                },
              })}
            />
          </Field>

          {/* <Field label="Estado">
            <Input
              {...form.register("state")}
              readOnly
              placeholder="Gerado automaticamente"
            />
          </Field>
          <Field label="Cidade">
            <Input
              type="text"
              placeholder="Gerado automaticamente"
              readOnly
              {...form.register("city")}
            />
          </Field> */}

          <Field label="Bairro">
            <Input
              type="text"
              placeholder="Gerado automaticamente"
              readOnly
              {...form.register("province")}
            />
          </Field>
          <Field label="Endereço">
            <Input
              type="text"
              placeholder="Gerado automaticamente"
              readOnly
              {...form.register("street")}
            />
          </Field>

          <Field label="Nota de Corte">
            <Input
              type="number"
              min="0"
              max="10"
              placeholder="Nota de corte"
              {...form.register("cutoff_score")}
            />
          </Field>
        </div>
      </div>

      <div className="grid grid-rows-[auto_1fr_auto] flex-1">
        <h4 className="text-lg font-medium font-alternative text-[#5B5B5B] mb-8">
          Redefinir Senha
        </h4>

        <div className="space-y-5">
          <Field label="Senha Atual">
            <Input
              type="password"
              placeholder="Informe sua senha atual"
              {...form.register("current_password")}
            />
          </Field>

          <Field label="Nova Senha">
            <Input
              type="password"
              placeholder="Informe sua nova senha"
              {...form.register("new_password")}
            />
          </Field>

          <Field label="Confirmar Senha">
            <Input
              type="password"
              placeholder="Confirme a nova senha"
              {...form.register("new_password_confirmation")}
            />
          </Field>
        </div>

        <Button
          variant="alternative-1"
          className="mx-auto h-10 w-60"
          loading={updateUserMutation.isPending}
        >
          Salvar
        </Button>
      </div>
    </form>
  );
}
